/** 管理台多标签页状态(zustand persist,key: tabbar-storage).
 * 复刻 yqbf-next store/tabbar.ts:首标签"工作台"固定不可关;
 * 关闭激活标签激活左邻居(无左邻居取右)。
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AdminTab {
  path: string;
  title: string;
  closable?: boolean;
}

const HOME_TAB: AdminTab = {
  path: "/admin/dashboard",
  title: "工作台",
  closable: false,
};

interface TabbarState {
  tabs: AdminTab[];
  activeTab: string;
  addTab: (tab: AdminTab) => void;
  removeTab: (path: string) => void;
  removeOtherTabs: (path: string) => void;
  removeLeftSide: (path: string) => void;
  removeRightSide: (path: string) => void;
  setActiveTab: (path: string) => void;
}

export const useTabbarStore = create<TabbarState>()(
  persist(
    (set) => ({
      tabs: [{ ...HOME_TAB }],
      activeTab: HOME_TAB.path,

      addTab: (tab) =>
        set((state) => {
          const exists = state.tabs.find((t) => t.path === tab.path);
          if (exists) {
            return { activeTab: tab.path };
          }
          return {
            tabs: [...state.tabs, { closable: tab.path !== HOME_TAB.path, ...tab }],
            activeTab: tab.path,
          };
        }),

      removeTab: (path) =>
        set((state) => {
          const tabs = state.tabs.filter((t) => t.path !== path);
          const removedIndex = state.tabs.findIndex((t) => t.path === path);

          // 关闭的是当前激活标签:激活左邻居,无左邻居取右
          let newActiveTab = state.activeTab;
          if (path === state.activeTab) {
            if (removedIndex > 0) {
              newActiveTab = tabs[removedIndex - 1]?.path || tabs[0]?.path;
            } else {
              newActiveTab = tabs[0]?.path || HOME_TAB.path;
            }
          }

          return { tabs, activeTab: newActiveTab };
        }),

      removeOtherTabs: (path) =>
        set((state) => ({
          tabs: state.tabs.filter((t) => t.path === path || !t.closable),
          activeTab: path,
        })),

      removeLeftSide: (path) =>
        set((state) => {
          const currentIndex = state.tabs.findIndex((t) => t.path === path);
          if (currentIndex === -1) return state;
          const tabs = state.tabs.filter((t, index) => index >= currentIndex || !t.closable);
          return { tabs, activeTab: path };
        }),

      removeRightSide: (path) =>
        set((state) => {
          const currentIndex = state.tabs.findIndex((t) => t.path === path);
          if (currentIndex === -1) return state;
          const tabs = state.tabs.filter((t, index) => index <= currentIndex || !t.closable);
          return { tabs, activeTab: path };
        }),

      setActiveTab: (path) => set({ activeTab: path }),
    }),
    {
      name: "tabbar-storage",
      partialize: (s) => ({ tabs: s.tabs, activeTab: s.activeTab }),
      merge: (persisted, current) => {
        const p = persisted as Partial<TabbarState> | undefined;
        const tabs = p?.tabs?.length ? [...p.tabs] : [...current.tabs];
        // 兜底:持久化数据损坏时也保证固定首标签存在
        if (!tabs.some((t) => t.path === HOME_TAB.path)) {
          tabs.unshift({ ...HOME_TAB });
        }
        return { ...current, ...p, tabs };
      },
    },
  ),
);
